import * as Mapbox from 'mapbox-gl'
import * as THREE from 'three'

export type Coordinates = { lng: number, lat: number, alt: number }

export type MapConfiguration = {
  center: { lng: number, lat: number }
  map: Mapbox.Map
}

type MapPointCloudGridModel = {
  type?: string | null
  anchor_mode?: string | null
  centering_origin?: [number, number, number] | null
  bounding_box_min?: [number, number, number] | null
  anchor_centering_origin_wgs84?: [number, number, number] | null
  anchor_bbox_min_wgs84?: [number, number, number] | null
}

export type MapPointCloudGridTransform = {
  yawRadians: number
  horizontalScale: number
}

/**
 * @param longitude Geographic coordinates (non-Mercator)
 * @returns Scene position in terms of x,y,z
 */
export function getScenePositionFromMapCoords(center: Mapbox.LngLatLike, longitude: number, latitude: number, altitude: number) {
    const measurePointMercatorCoordinates = Mapbox.MercatorCoordinate.fromLngLat([longitude, latitude], altitude)
    const sceneMercatorCoordinates = Mapbox.MercatorCoordinate.fromLngLat(center, 0)
    const mercatorMeter = sceneMercatorCoordinates.meterInMercatorCoordinateUnits()

    /** 
    * IMPORTANT NOTEs: 
    * 1. In geographic coordiantes, Z is UP, but in our Scenes, Y is up. 
    * 2. Converting from Geographic coordinates to Scene coordinates involves converting to Mercator coordinates as an intermediary step. 
    */
    const mx = (measurePointMercatorCoordinates.x - sceneMercatorCoordinates.x) / mercatorMeter // Longitude
    const my = (measurePointMercatorCoordinates.z - sceneMercatorCoordinates.z) / mercatorMeter // Latitude
    const mz = (measurePointMercatorCoordinates.y - sceneMercatorCoordinates.y) / mercatorMeter // Altitude

    return new THREE.Vector3(mx, my, mz)
}

/**
 * Derives the horizontal source-grid correction needed to align a projected
 * point cloud with the Mapbox scene. The two source anchors define the point
 * cloud's local grid vector, while their WGS84 counterparts define the same
 * vector in the scene's Mercator tangent plane.
 */
export function resolveMapPointCloudGridTransform(
  sceneOrigin: Mapbox.LngLatLike,
  model: MapPointCloudGridModel,
): MapPointCloudGridTransform | null {
  if (model?.type !== 'point-cloud' || model.anchor_mode === 'custom') return null

  const sourceCenter = model.centering_origin
  const sourceMin = model.bounding_box_min
  const wgs84Center = model.anchor_centering_origin_wgs84
  const wgs84Min = model.anchor_bbox_min_wgs84
  const anchors = [sourceCenter, sourceMin, wgs84Center, wgs84Min]
  if (anchors.some(anchor => !anchor || anchor.length < 2 || !anchor.slice(0, 2).every(Number.isFinite))) return null

  // The existing point-cloud X rotation maps source (x, y) to scene (x, -z).
  const sourceX = sourceMin[0] - sourceCenter[0]
  const sourceZ = -(sourceMin[1] - sourceCenter[1])
  const sourceLength = Math.hypot(sourceX, sourceZ)
  if (sourceLength < Number.EPSILON) return null

  const sceneCenter = getScenePositionFromMapCoords(sceneOrigin, wgs84Center[0], wgs84Center[1], 0)
  const sceneMin = getScenePositionFromMapCoords(sceneOrigin, wgs84Min[0], wgs84Min[1], 0)
  const targetX = sceneMin.x - sceneCenter.x
  const targetZ = sceneMin.z - sceneCenter.z
  const targetLength = Math.hypot(targetX, targetZ)
  if (!Number.isFinite(targetLength) || targetLength < Number.EPSILON) return null

  const sourceAngle = Math.atan2(sourceZ, sourceX)
  const targetAngle = Math.atan2(targetZ, targetX)
  let angleDelta = targetAngle - sourceAngle
  angleDelta = Math.atan2(Math.sin(angleDelta), Math.cos(angleDelta))

  // THREE.js positive Y rotation is clockwise in the scene's x/z plane.
  return {
    yawRadians: -angleDelta,
    horizontalScale: targetLength / sourceLength,
  }
}

/**
 * @param position Scene position in terms of x,y,z
 * @returns Geographic coordinates (non-Mercator)
 */
export function getMapCoordsFromScenePosition(position: THREE.Vector3, center: Mapbox.LngLatLike): Coordinates {
    const sceneMercatorCoordinates = Mapbox.MercatorCoordinate.fromLngLat(center, 0)

    /** 
     * IMPORTANT NOTEs: 
     * 1. In geographic coordiantes, Z is UP, but in our Scenes, Y is up. 
     * 2. Converting from Scene coordinates to Geographic coordinates involves converting to Mercator coordinates as an intermediary step. 
     **/
    const mx = position.x * sceneMercatorCoordinates.meterInMercatorCoordinateUnits() + sceneMercatorCoordinates.x;
    const my = position.y * sceneMercatorCoordinates.meterInMercatorCoordinateUnits() + sceneMercatorCoordinates.z;
    const mz = position.z * sceneMercatorCoordinates.meterInMercatorCoordinateUnits() + sceneMercatorCoordinates.y;

    const mercatorCoordinate = new Mapbox.MercatorCoordinate(mx, mz, my);

    return { ...mercatorCoordinate.toLngLat(), alt: mercatorCoordinate.toAltitude() }
}

/* Given a query in the form "lng, lat" or "lat, lng"
 * returns the matching geographic coordinate(s)
 * as search results in carmen geojson format,
 * https://github.com/mapbox/carmen/blob/master/carmen-geojson.md */
export function coordinatesGeocoder(query: string) {
  // Match anything which looks like
  // decimal degrees coordinate pair.
  const matches = query.match(
    /^[ ]*(?:Lng: )?(-?\d+\.?\d*)[, ]+(?:Lat: )?(-?\d+\.?\d*)[ ]*$/i
  )
  if (!matches) {
    return null
  }
  function coordinateFeature(lng, lat, order) {
    if (order == "lng/lat") {
      return {
        center: [lng, lat],
        geometry: {
          type: 'Point',
          coordinates: [lng, lat]
        },
        place_name: 'Lng: ' + lng + ' Lat: ' + lat,
        place_type: ['coordinate'],
        properties: {},
        type: 'Feature'
      }
    } else {
      return {
        center: [lng, lat],
        geometry: {
          type: 'Point',
          coordinates: [lng, lat]
        },
        place_name: 'Lat: ' + lat + ' Lng: ' + lng,
        place_type: ['coordinate'],
        properties: {},
        type: 'Feature'
      }
    }
  }
  
  const coord1 = Number(matches[1])
  const coord2 = Number(matches[2])
  const geocodes = []
  
  if ((coord2 >= -90 && coord2 <= 90) && (coord1 >= -180 && coord1 <= 180)) {
    // must be lng, lat
    geocodes.push(coordinateFeature(coord1, coord2, "lng/lat"))
  } else if ((coord1 >= -90 && coord1 <= 90) && (coord2 >= -180 && coord2 <= 180)) {
    // must be lat, lng
    geocodes.push(coordinateFeature(coord2, coord1, "lat/lng"))
  }

  return geocodes
}
